// src/features/projects/briefing/ProjectBriefingPeek.jsx
//
// Lightweight list-row briefing peek for the Projects Portfolio landing.
// Left: "The record" (owner, spend of budget + signed variance, next open
// decision, open risks / decisions due). Right: current-week track updates
// from the shared canonical overview. Risks are the only lazy per-project
// read and run only for the open peek.

import React, { useEffect, useState } from "react";
import { T } from "../../../app/tokens.js";
import { getProjectRisks, getProjectDecisions } from "../../../data/projectsClient.js";
import { formatRecordAmount, recordAmount, recordVariance } from "../projectRecord.js";
import { trackLabel, resolveLine } from "../weekly/weeklyModel.js";
import { TrackCompleteness } from "../weekly/TrackCompleteness.jsx";
import { utcDay } from "../weekly/ProjectWorkingUpdatesView.jsx";
import { fmtDate } from "../../../utils/format.js";
import {
  decisionsDueInWeek, findOverviewProject, nextOpenDecision, peekTrackUpdates,
} from "./landingModel.js";

const sx = {
  wrap: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 22, padding: "14px 18px 6px" },
  head: { fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".07em", textTransform: "uppercase", color: T.mid, marginBottom: 8 },
  dl: { display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 14, rowGap: 6, margin: 0, fontSize: T.fsSm },
  dt: { color: T.mid },
  dd: { margin: 0, color: T.dark, minWidth: 0 },
  muted: { color: T.light },
  item: { padding: "7px 0", borderTop: `1px solid ${T.projectsHubLine}` },
  track: { fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark },
  note: { fontSize: T.fsSm, color: T.dark, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  struck: { color: T.light, textDecoration: "line-through" },
  by: { fontSize: T.fsXxs, color: T.mid, marginTop: 2 },
  panel: { fontSize: T.fsSm, color: T.mid, background: T.hoverBg, borderRadius: 8, padding: "8px 10px" },
  err: { fontSize: T.fsSm, color: T.red, background: T.alertErrBg, border: `1px solid ${T.alertErrBorder}`, borderRadius: 8, padding: "8px 10px" },
  skel: { height: 12, borderRadius: 6, background: T.lightGray, margin: "8px 0" },
  actions: { display: "flex", gap: 8, justifyContent: "flex-end", padding: "8px 18px 14px", flexWrap: "wrap" },
  primary: { border: "none", background: T.projectsHubNavy, color: T.white, borderRadius: 8, padding: "7px 13px", fontSize: T.fsSm, fontWeight: T.fwBold, cursor: "pointer" },
  ghost: { border: `1px solid ${T.projectsPeekHair}`, background: T.card, color: T.dark, borderRadius: 8, padding: "7px 13px", fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: "pointer" },
  retry: { marginLeft: 8, fontSize: T.fsXs, fontWeight: T.fwBold, color: T.red, background: "none", border: `1px solid ${T.alertErrBorder}`, borderRadius: 6, padding: "1px 7px", cursor: "pointer" },
};

function Skeleton() {
  return <div data-testid="peek-week-loading">{[80, 60].map((w) => <div key={w} style={{ ...sx.skel, width: `${w}%` }} />)}</div>;
}

function useOpenRisks(projectId, refreshToken) {
  const [state, setState] = useState({ id: null, loading: true, error: null, count: null });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ id: projectId, loading: true, error: null, count: null });
    Promise.resolve().then(() => getProjectRisks(projectId))
      .then((rows) => {
        if (!active) return;
        const list = Array.isArray(rows) ? rows : [];
        setState({ id: projectId, loading: false, error: null, count: list.filter((r) => !r?.deletedAt && (r?.status == null || r.status === "open")).length });
      })
      .catch((err) => { if (active) setState({ id: projectId, loading: false, error: err?.message || "Could not load risks.", count: null }); });
    return () => { active = false; };
  }, [projectId, refreshToken, retry]);
  return [state.id === projectId ? state : { loading: true }, () => setRetry((n) => n + 1)];
}

// Only used when the project is outside the canonical weekly population;
// otherwise decisions come from the shared overview (no extra read).
function useFallbackDecisions(projectId, enabled, refreshToken) {
  const [state, setState] = useState({ id: null, loading: true, error: null, rows: null });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setState({ id: projectId, loading: true, error: null, rows: null });
    Promise.resolve().then(() => getProjectDecisions(projectId))
      .then((rows) => { if (active) setState({ id: projectId, loading: false, error: null, rows: (Array.isArray(rows) ? rows : []).filter((d) => !d?.deletedAt) }); })
      .catch((err) => { if (active) setState({ id: projectId, loading: false, error: err?.message || "Could not load decisions.", rows: null }); });
    return () => { active = false; };
  }, [projectId, enabled, refreshToken, retry]);
  return [state.id === projectId ? state : { loading: true }, () => setRetry((n) => n + 1)];
}

function TrackLine({ line }) {
  const r = resolveLine(line);
  const author = typeof line.authorName === "string" && line.authorName.trim() ? line.authorName.trim() : "Unattributed";
  const day = utcDay(line.createdAt);
  return (
    <div style={sx.item} data-testid={`peek-line-${line.id}`}>
      <div style={sx.track}>{trackLabel(line.trackKey)}</div>
      <div style={sx.note} title={r.struck ? r.original : r.current || r.original}>
        {r.corrected ? (
          r.struck
            ? <><s style={sx.struck}>{r.original}</s> <span style={{ color: T.mid }}>Struck</span></>
            : <><s style={sx.struck}>{r.original}</s> {r.current}</>
        ) : r.original}
      </div>
      <div style={sx.by}>{author}{line.authorOrg ? `, ${line.authorOrg}` : ""}{day ? ` · ${day} (UTC)` : " · Day unavailable"}</div>
    </div>
  );
}

export function ProjectBriefingPeek({ project, weekState, onRetryWeek, canSeeBudget, onOpenFull, onCollapse, refreshToken = 0 }) {
  const [risks, retryRisks] = useOpenRisks(project.id, refreshToken);
  const overview = weekState?.data ? findOverviewProject(weekState.data, project.id) : null;
  const weekDate = weekState?.data?.weekDate;
  const weekReady = Boolean(weekState && !weekState.loading && !weekState.error && weekState.data);

  // Record column
  const currency = project.currency || project.budgetCurrency || "USD";
  const budget = recordAmount(project.approvedBudget);
  const spent = recordAmount(project.spentToDate);
  const variance = recordVariance(project);
  const absent = weekReady && !overview;
  const [fallback, retryDecisions] = useFallbackDecisions(project.id, absent, refreshToken);
  const decisionRows = overview ? overview.decisions : absent && !fallback.loading && !fallback.error ? fallback.rows : null;
  const decision = decisionRows ? nextOpenDecision(decisionRows) : null;
  const dueCount = decisionRows && weekDate ? decisionsDueInWeek(decisionRows, weekDate).length : null;
  const owner = (typeof project.internalOwner === "string" && project.internalOwner.trim()) || (overview?.owner && overview.owner !== "Not assigned" ? overview.owner : null);

  let decisionNode;
  if (!weekState || weekState.loading) decisionNode = <span style={sx.muted}>Loading</span>;
  else if (weekState.error) decisionNode = <span style={sx.muted}>Unavailable</span>;
  else if (absent && fallback.loading) decisionNode = <span style={sx.muted}>Loading</span>;
  else if (absent && fallback.error) decisionNode = (
    <span role="alert" style={{ color: T.red }} data-testid="peek-decisions-error">
      {fallback.error}
      <button type="button" style={sx.retry} onClick={retryDecisions} data-testid="peek-decisions-retry">Retry</button>
    </span>
  );
  else if (!decision) decisionNode = <span style={sx.muted}>No open decisions</span>;
  else decisionNode = <>{decision.title || decision.name || "Untitled decision"}{decision.neededByDate ? <span style={{ color: T.mid }}> · needed by {fmtDate(decision.neededByDate)}</span> : null}</>;

  const riskText = risks.loading ? "…" : risks.error ? "?" : String(risks.count);
  const dueText = dueCount == null ? (weekState?.error || fallback.error ? "?" : "…") : String(dueCount);

  // Week column
  let week;
  if (!weekState || weekState.loading) week = <Skeleton />;
  else if (weekState.error) week = (
    <div role="alert" style={sx.err} data-testid="peek-week-error">
      {typeof weekState.error === "string" ? weekState.error : "Could not load current-week updates."}
      {onRetryWeek && <button type="button" style={sx.retry} onClick={onRetryWeek} data-testid="peek-week-retry">Retry</button>}
    </div>
  );
  else if (!overview) week = <div style={sx.panel} data-testid="peek-week-absent">This project is not part of this week's canonical Weekly Review.</div>;
  else {
    const lines = peekTrackUpdates(overview, weekDate, 2);
    week = lines.length === 0
      ? <div style={sx.panel} data-testid="peek-week-empty">No updates posted this week.</div>
      : <div data-testid="peek-week-lines">{lines.map((l) => <TrackLine key={l.id} line={l} />)}</div>;
  }

  return (
    <div data-testid={`project-peek-${project.id}`} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <div style={sx.wrap}>
        <section aria-label="The record" data-testid="peek-record">
          <div style={sx.head}>The record</div>
          <dl style={sx.dl}>
            <dt style={sx.dt}>Owner</dt>
            <dd style={sx.dd} data-testid="peek-owner">{owner || <span style={sx.muted}>Not assigned</span>}</dd>
            {canSeeBudget && <>
              <dt style={sx.dt}>Spend</dt>
              <dd style={sx.dd} data-testid="peek-spend" title="Variance is approved budget minus forecast at completion, in the project currency.">
                {formatRecordAmount(spent, currency)} of {formatRecordAmount(budget, currency)}
                {" · "}
                <span style={{ color: variance == null ? T.light : variance < 0 ? T.red : variance > 0 ? T.green : T.mid }}>
                  {variance == null ? "variance unknown" : `${formatRecordAmount(variance, currency, true)} variance`}
                </span>
              </dd>
            </>}
            <dt style={sx.dt}>Next decision</dt>
            <dd style={sx.dd} data-testid="peek-next-decision">{decisionNode}</dd>
            <dt style={sx.dt}>Exposure</dt>
            <dd style={sx.dd} data-testid="peek-exposure">
              {riskText} open risk{risks.count === 1 ? "" : "s"} · {dueText} decision{dueCount === 1 ? "" : "s"} due
              {risks.error && (
                <div role="alert" style={{ color: T.red, marginTop: 3 }} data-testid="peek-risks-error">
                  {risks.error}
                  <button type="button" style={sx.retry} onClick={retryRisks} data-testid="peek-risks-retry">Retry</button>
                </div>
              )}
            </dd>
          </dl>
        </section>
        <section aria-label="This week" data-testid="peek-week">
          <div style={sx.head} data-testid="peek-week-head">
            This week
          </div>
          {overview && weekReady && <div style={{ marginBottom: 8 }}><TrackCompleteness project={overview} surface="peek" /></div>}
          {week}
        </section>
      </div>
      <div style={sx.actions}>
        <button type="button" className="projects-peek-btn" style={sx.ghost} onClick={onCollapse} data-testid={`project-peek-collapse-${project.id}`}>Collapse</button>
        <button type="button" className="projects-peek-btn" style={sx.primary} onClick={onOpenFull} data-testid={`project-peek-open-full-${project.id}`}>Open full project →</button>
      </div>
    </div>
  );
}
